// A row (or a card) that can be swiped on a phone: left to archive, right to snooze. Touch and pen
// only (a mouse never swipes), `touch-action: pan-y` so the page still scrolls (and zooms), and the
// rules in ./swipe.ts so scrolling never turns into a swipe. Letting go past the line runs the
// action; the tap that ends a swipe never opens the row. The buttons stay the main way; this is
// a shortcut for one hand.
import { useRef, useState, type PointerEvent, type ReactNode } from "react";
import { Archive, Clock } from "lucide-react";
import { cn } from "@web/lib/cn";
import { useMediaQuery } from "@web/lib/useMediaQuery";
import {
  commitDistance,
  followOffset,
  NO_SWIPE_SELECTOR,
  trackEnd,
  trackMove,
  trackStart,
  type SwipeDirection,
  type SwipeTrack,
} from "./swipe";

export const PHONE_QUERY = "(max-width: 767px)";

export function usePhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

export interface SwipeRowProps {
  enabled: boolean;
  /** Null when this row has no swipe-able archive. */
  onArchive: (() => void) | null;
  onSnooze: (() => void) | null;
  /** "Archive", or the feed's cleanup label. */
  archiveLabel?: string;
  snoozeLabel?: string;
  className?: string;
  children: ReactNode;
}

export function SwipeRow({
  enabled,
  onArchive,
  onSnooze,
  archiveLabel = "Archive",
  snoozeLabel = "Snooze",
  className,
  children,
}: SwipeRowProps) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [width, setWidth] = useState(0);
  const track = useRef<SwipeTrack | null>(null);
  const pointer = useRef<number | null>(null);
  const swallowClick = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const allowed = { archive: !!onArchive, snooze: !!onSnooze };
  const active = enabled && (allowed.archive || allowed.snooze);

  const reset = () => {
    track.current = null;
    pointer.current = null;
    setDragging(false);
    setOffset(0);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!active || e.pointerType === "mouse" || !e.isPrimary) return;
    if ((e.target as Element).closest(NO_SWIPE_SELECTOR)) return;
    track.current = trackStart(e.clientX, e.clientY);
    pointer.current = e.pointerId;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!track.current || pointer.current !== e.pointerId) return;
    const next = trackMove(track.current, e.clientX, e.clientY);
    track.current = next;
    if (next.axis === "y") {
      reset(); // a scroll: let the page have it
      return;
    }
    if (next.axis !== "x") return;
    const rowWidth = box.current?.offsetWidth ?? 0;
    if (!dragging) {
      setDragging(true);
      setWidth(rowWidth);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    setOffset(followOffset(next.dx, rowWidth, allowed));
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!track.current || pointer.current !== e.pointerId) return;
    const swiped = track.current.axis === "x";
    const outcome: SwipeDirection | null = trackEnd(
      track.current,
      box.current?.offsetWidth ?? 0,
      allowed,
    );
    if (swiped) {
      // The click a browser may send after the lift is part of the swipe, not a tap.
      swallowClick.current = true;
      setTimeout(() => (swallowClick.current = false), 400);
    }
    reset();
    if (outcome === "archive") onArchive?.();
    if (outcome === "snooze") onSnooze?.();
  };

  const past = Math.abs(offset) >= commitDistance(width);

  return (
    <div ref={box} className={cn("relative overflow-hidden", className)}>
      {offset !== 0 ? (
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 flex items-center px-5 text-sm font-medium",
            offset > 0 ? "justify-start" : "justify-end",
            past ? "bg-accent-soft text-ink" : "bg-sunk text-muted",
          )}
        >
          {offset > 0 ? (
            <span className="inline-flex items-center gap-2">
              <Clock className="size-4" />
              {snoozeLabel}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              {archiveLabel}
              <Archive className="size-4" />
            </span>
          )}
        </div>
      ) : null}
      <div
        className={cn("relative", !dragging && "transition-transform duration-200 ease-out")}
        style={{
          transform: offset ? `translateX(${offset}px)` : undefined,
          touchAction: active ? "pan-y pinch-zoom" : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={reset}
        onClickCapture={(e) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}
